"use client";

import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import Block from "@/components/elements/Block";
import Container from "@/components/elements/Container";
import Heading2 from "@/components/elements/Heading2";

// BL-513: the focus ring moved to a has-[] variant because the card is no
// longer the link — the link inside it is. Same ring, same trigger, drawn by
// the wrapper instead of by the focused element.
const BASE_CARD_CLASS =
  "grid p-4 gap-x-2 no-underline border-2 bg-white rounded-xl lg:grid-cols-[3fr_1fr] lg:grid-rows-1 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-primary has-[a:focus-visible]:outline-none";

const ProductTabs = ({ block = {}, position }) => {
  const { slug: currentSlug } = useParams();

  const { product_tabs: productsTabs = [], heading, settings = {} } = block;
  const textColor = settings.text_color?.color_selector || "text-primary";
  const sectionId = `product-tabs-${position !== undefined ? position : "default"}`;

  if (!productsTabs?.length) return null;

  return (
    <Block name="product_tabs" id={sectionId} settings={settings}>
      <Container>
        {heading ? (
          <Heading2
            color={settings?.text_color?.color_selector || "text-primary"}
            className="leading-tight text-3xl md:text-4xl lg:text-5xl text-center mb-6"
          >
            {heading}
          </Heading2>
        ) : null}

        <div
          className="grid md:grid-cols-3 gap-4 pt-5 pb-8 lg:pt-5 lg:pb-12 xl:pt-5 xl:pb-16"
        >
          {productsTabs.map((productTab, index) => {
            const product = productTab.reference?.[0];
            if (!product) return null;

            const tabSlug = product.url?.split("/").filter(Boolean).pop();
            const isActive = !!currentSlug && !!tabSlug && tabSlug === currentSlug;
            const cardKey = productTab._metadata?.uid ?? index;
            const cardClass = `${BASE_CARD_CLASS} ${isActive ? "border-primary" : "border-white"}`;
            const imageUrl = productTab.product_image?.url ?? null;
            const imageAlt =
              productTab.product_image?.description || product.product_name || "";

            // BL-513: Crownpeak measures the raw link text, not the accessible
            // name, so an aria-label on the card-wide link changed nothing for
            // it. The name is now the only thing inside the link, which covers
            // the card with an ::after overlay to keep it clickable in full.
            const renderCard = (nameNode) => (
              <>
                <div className="flex flex-col lg:justify-center order-2 lg:order-0 lg:grid-area-1/1/2/2">
                  <h3 className={`${textColor} font-semibold font-worksans md:text-xl text-lg`}>
                    {nameNode}
                  </h3>
                  <p className={`${textColor} font-worksans text-sm hidden md:block`}>
                    {productTab.product_description}
                  </p>
                </div>

                {imageUrl && (
                  <div className="relative hidden order-1 w-1/3 h-10 lg:h-auto md:block lg:w-full">
                    <Image
                      src={imageUrl}
                      alt={imageAlt}
                      fill
                      sizes="(min-width: 768px) 33vw, 33vw"
                      className="object-contain md:pb-2 lg:pb-0"
                    />
                  </div>
                )}
              </>
            );

            if (isActive) {
              return (
                <div key={cardKey} className={cardClass} aria-current="page">
                  {renderCard(product.product_name)}
                </div>
              );
            }

            return product.url ? (
              <div key={cardKey} className={`${cardClass} relative`}>
                {renderCard(
                  <Link
                    href={product.url}
                    // outline-none because the ring is drawn by the card via
                    // has-[a:focus-visible]; without this the browser also
                    // outlines the name and the card shows two indicators.
                    className="no-underline focus-visible:outline-none after:absolute after:inset-0 after:z-10 after:content-['']"
                  >
                    {product.product_name}
                  </Link>
                )}
              </div>
            ) : (
              <div key={cardKey} className={cardClass}>
                {renderCard(product.product_name)}
              </div>
            );
          })}
        </div>
      </Container>
    </Block>
  );
};

export default ProductTabs;
